import { Routes, Route, useLocation, Outlet } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './context/ThemeContext';
import useMobileDetect from '@/hooks/useMobileDetect';
import Navigation from '@/components/Navigation';
import MobileBottomNav from '@/components/MobileBottomNav';
import Footer from '@/components/Footer';
import NavV2 from '@/components/v2/NavV2';
import FooterV2 from '@/components/v2/FooterV2';
import FooterV3 from '@/components/v3/FooterV3';
import ScrollProgress from '@/components/ScrollProgress';
import BookFAB from '@/components/BookFAB';

// V3 pages (primary — lazy loaded)
const HomeV3 = lazy(() => import('@/pages/v3/HomeV3'));
const ServicesV3 = lazy(() => import('@/pages/v3/ServicesV3'));
const AboutV3 = lazy(() => import('@/pages/v3/AboutV3'));
const ContactV3 = lazy(() => import('@/pages/v3/ContactV3'));
const FAQV3 = lazy(() => import('@/pages/v3/FAQV3'));
const BookV3 = lazy(() => import('@/pages/v3/BookV3'));
const WaterAerobicsV3 = lazy(() => import('@/pages/v3/WaterAerobicsV3'));
const ScholarshipV3 = lazy(() => import('@/pages/v3/ScholarshipV3'));

// V1 & V2 pages (legacy — lazy loaded)
const Home = lazy(() => import('@/pages/Home'));
const About = lazy(() => import('@/pages/About'));
const Services = lazy(() => import('@/pages/Services'));
const Projects = lazy(() => import('@/pages/Projects'));
const Contact = lazy(() => import('@/pages/Contact'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const Referrals = lazy(() => import('@/pages/Referrals'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const HomeV2 = lazy(() => import('@/pages/v2/HomeV2'));
const ServicesV2 = lazy(() => import('@/pages/v2/ServicesV2'));
const AboutV2 = lazy(() => import('@/pages/v2/AboutV2'));
const ContactV2 = lazy(() => import('@/pages/v2/ContactV2'));

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // The target section may mount after a lazy chunk + page transition,
      // so retry a few times before giving up and scrolling to top.
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (attempts++ < 10) {
          window.setTimeout(tryScroll, 80);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

function PageFade({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18, ease: 'linear' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function V1Layout() {
  const isMobile = useMobileDetect();
  return (
    <>
      <Navigation />
      <main className={`flex-grow ${isMobile ? 'pb-20' : ''}`}>
        <Outlet />
      </main>
      <Footer />
      {isMobile && <MobileBottomNav />}
    </>
  );
}

function V2Layout() {
  return (
    <>
      <NavV2 />
      <main className="flex-grow">
        <Outlet />
      </main>
      <FooterV2 />
    </>
  );
}

function V3Layout() {
  return (
    <>
      <ScrollProgress />
      <Navigation />
      <main className="flex-grow bg-white pt-0">
        <PageFade>
          <Outlet />
        </PageFade>
      </main>
      <FooterV3 />
      <BookFAB />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-white text-v3-text">
        <ScrollToTop />
        <Suspense fallback={null}>
        <Routes>
          {/* V3 routes — promoted to root for preview */}
          <Route element={<V3Layout />}>
            <Route path="/" element={<HomeV3 />} />
            <Route path="/services" element={<ServicesV3 />} />
            <Route path="/about" element={<AboutV3 />} />
            <Route path="/contact" element={<ContactV3 />} />
            <Route path="/faq" element={<FAQV3 />} />
            <Route path="/book" element={<BookV3 />} />
            <Route path="/water-aerobics" element={<WaterAerobicsV3 />} />
            <Route path="/scholarship" element={<ScholarshipV3 />} />
          </Route>

          {/* V1 routes — moved to /v1 for reference */}
          <Route element={<V1Layout />}>
            <Route path="/v1" element={<Home />} />
            <Route path="/v1/about" element={<About />} />
            <Route path="/v1/services" element={<Services />} />
            <Route path="/v1/projects" element={<Projects />} />
            <Route path="/v1/contact" element={<Contact />} />
            <Route path="/v1/faq" element={<FAQ />} />
            <Route path="/v1/referrals" element={<Referrals />} />
          </Route>

          {/* V2 routes — preserved at /v2 */}
          <Route element={<V2Layout />}>
            <Route path="/v2" element={<HomeV2 />} />
            <Route path="/v2/services" element={<ServicesV2 />} />
            <Route path="/v2/about" element={<AboutV2 />} />
            <Route path="/v2/contact" element={<ContactV2 />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </div>
    </ThemeProvider>
  );
}

export default App;
